/**
 * The floating island's renderable half (AD-840): turns the pure arrays of
 * `island.ts` into three objects under one group. Materials are stock
 * (`MeshStandardMaterial` / `MeshBasicMaterial`), which three's WebGPU renderer
 * converts to node materials itself, so the WebGL2 and WebGPU paths draw the same
 * island from the same code. Nothing here is a collider or a raycast target —
 * the walkable edge is the shared sim's coastline, never these meshes.
 */

import * as THREE from 'three';

import {
  cloudSeaPlan,
  cloudSeaTexture,
  distantIslandArrays,
  distantIslands,
  islandRockInstances,
  islandRootInstances,
  islandTopArrays,
  islandUndersideArrays,
  type HeightAt,
  type IslandDetail,
  type IslandInstance,
  type IslandPlan,
  type MeshArrays,
} from './island.js';

/** The built island: its group, and the cloud textures `animate` drifts. */
export interface IslandHandle {
  group: THREE.Group;
  /** The top mesh (the zone's ground). */
  top: THREE.Mesh;
  /** Drift the cloud layers (seconds; held at 0 for still captures). */
  animate(timeSec: number): void;
}

/** Cloud-texture edge, texels (tileable; 128² ⇒ 64 KB, generated once per join). */
const CLOUD_TEXTURE_SIZE = 128;
/** World metres per cloud-texture repeat. */
const CLOUD_TILE_M = 96;

/** The sRGB transfer function, inverted (three's own maths; it is not exported from 'three'). */
function srgbToLinear(c: number): number {
  return c < 0.04045 ? c * 0.0773993808 : Math.pow(c * 0.9478672986 + 0.0521327014, 2.4);
}

function geometryOf(arrays: MeshArrays): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(arrays.positions, 3));
  if (arrays.colors) {
    // The palette is authored in sRGB (island.ts); vertex colours are read as LINEAR, so an
    // unconverted 0.4 lands as ~0.66 on screen and every rock washes out to pale beige.
    const linear = Float32Array.from(arrays.colors, srgbToLinear);
    geometry.setAttribute('color', new THREE.BufferAttribute(linear, 3));
  }
  geometry.setIndex(new THREE.BufferAttribute(arrays.indices, 1));
  if (arrays.normals) geometry.setAttribute('normal', new THREE.BufferAttribute(arrays.normals, 3));
  else geometry.computeVertexNormals();
  geometry.computeBoundingSphere();
  return geometry;
}

function rockMaterial(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    vertexColors: true,
    flatShading: true,
    roughness: 0.95,
    metalness: 0,
  });
}

function instanced(
  geometry: THREE.BufferGeometry,
  material: THREE.Material,
  instances: readonly IslandInstance[],
  name: string,
): THREE.InstancedMesh {
  const mesh = new THREE.InstancedMesh(geometry, material, instances.length);
  mesh.name = name;
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const e = new THREE.Euler();
  const p = new THREE.Vector3();
  const s = new THREE.Vector3();
  const c = new THREE.Color();
  instances.forEach((inst, i) => {
    e.set(inst.tilt, inst.rotY, inst.tilt * 0.5);
    q.setFromEuler(e);
    p.set(inst.x, inst.y, inst.z);
    s.set(inst.sx, inst.sy, inst.sz);
    m.compose(p, q, s);
    mesh.setMatrixAt(i, m);
    mesh.setColorAt(i, c.setRGB(inst.color[0], inst.color[1], inst.color[2], THREE.SRGBColorSpace));
  });
  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  mesh.computeBoundingSphere();
  return mesh;
}

function cloudTexture(seed: number, repeat: number): THREE.DataTexture {
  const texture = new THREE.DataTexture(
    cloudSeaTexture(CLOUD_TEXTURE_SIZE, seed),
    CLOUD_TEXTURE_SIZE,
    CLOUD_TEXTURE_SIZE,
    THREE.RGBAFormat,
  );
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.generateMipmaps = true;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.repeat.set(repeat, repeat);
  texture.needsUpdate = true;
  return texture;
}

/**
 * Build the island: top (in the zone's ground material), underside, rim crags +
 * debris, roots, distant islands, cloud sea. `horizon` is the fog/horizon colour
 * the cloud sea is tinted toward so it melts into the sky. `topArrays` replaces
 * the ringed-fan top — a relief zone passes its heightfield cut to the lip rim
 * (terrain.ts `heightfieldClippedArrays`), so the top stays the seating surface.
 */
export function buildIsland(
  plan: IslandPlan,
  heightAt: HeightAt,
  detail: IslandDetail,
  groundMaterial: THREE.Material,
  horizon: string,
  topArrays?: MeshArrays,
): IslandHandle {
  const group = new THREE.Group();
  group.name = 'island';

  const top = new THREE.Mesh(
    geometryOf(topArrays ?? islandTopArrays(plan, heightAt)),
    groundMaterial,
  );
  top.name = 'island-top';
  top.receiveShadow = true;
  group.add(top);

  const under = new THREE.Mesh(
    geometryOf(islandUndersideArrays(plan, heightAt, detail)),
    rockMaterial(),
  );
  under.name = 'island-underside';
  group.add(under);

  const rocks = islandRockInstances(plan, heightAt, detail);
  if (rocks.length > 0) {
    const mesh = instanced(
      new THREE.IcosahedronGeometry(1, 0),
      new THREE.MeshStandardMaterial({ flatShading: true, roughness: 0.95, metalness: 0 }),
      rocks,
      'island-rocks',
    );
    mesh.castShadow = !detail.lite;
    group.add(mesh);
  }

  const roots = islandRootInstances(plan, heightAt, detail);
  if (roots.length > 0) {
    const cone = new THREE.ConeGeometry(0.07, 1, 5, 1);
    cone.rotateX(Math.PI); // tip down
    group.add(
      instanced(
        cone,
        new THREE.MeshStandardMaterial({ roughness: 1, metalness: 0 }),
        roots,
        'island-roots',
      ),
    );
  }

  const far = distantIslands(plan, detail);
  if (far.length > 0) {
    const mesh = new THREE.Mesh(geometryOf(distantIslandArrays(far, detail.lite)), rockMaterial());
    mesh.name = 'island-distant';
    group.add(mesh);
  }

  const textures: { texture: THREE.Texture; speed: number }[] = [];
  let baseY = 0;
  for (const p of plan.rim) baseY = Math.min(baseY, heightAt(p.x, p.z));
  const sea = cloudSeaPlan(plan, detail, baseY);
  if (sea) {
    const size = sea.halfSize * 2;
    const repeat = size / CLOUD_TILE_M;
    const tint = new THREE.Color(horizon).lerp(new THREE.Color('#ffffff'), 0.45);
    const floorTex = cloudTexture(plan.wire.seed, repeat);
    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(size, size),
      new THREE.MeshBasicMaterial({ map: floorTex, color: tint }),
    );
    floor.name = 'island-cloud-floor';
    floor.rotation.x = -Math.PI / 2;
    floor.position.set(sea.cx, sea.floorY, sea.cz);
    group.add(floor);
    textures.push({ texture: floorTex, speed: 0.0012 });
    if (sea.wispY !== null) {
      const wispTex = cloudTexture((plan.wire.seed ^ 0x27d4eb2d) >>> 0, repeat * 0.7);
      const wisps = new THREE.Mesh(
        new THREE.PlaneGeometry(size, size),
        new THREE.MeshBasicMaterial({
          map: wispTex,
          color: tint,
          transparent: true,
          opacity: 0.55,
          depthWrite: false,
        }),
      );
      wisps.name = 'island-cloud-wisps';
      wisps.rotation.x = -Math.PI / 2;
      wisps.position.set(sea.cx, sea.wispY, sea.cz);
      wisps.renderOrder = 1;
      group.add(wisps);
      textures.push({ texture: wispTex, speed: 0.0026 });
    }
  }

  return {
    group,
    top,
    animate(timeSec: number): void {
      for (const { texture, speed } of textures) {
        texture.offset.set(timeSec * speed, timeSec * speed * 0.37);
      }
    },
  };
}
